import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PowertrainBasicInfo, PowertrainAssetsConfiguration } from '../../../../SharedComponents';
import { ALLOWED_ASSET_TYPES, GENERIC_MACHINE_SUBTYPES } from '../PhysicalConnectionStep/constants';
import { UNMONITORED_GEARBOX_TYPE_CONFIG_ID } from '../../../constants';
import './PreviewStep.scss';

/**
 * PreviewStep - Final step showing configuration summary and handling submission
 * Displays all collected data for review before submission and handles add/edit operations
 */
const PreviewStep = ({
    stepData = {},
    onStepComplete = () => { },
    isActive = false,
    mode = 'create',
    allStepData = {}, // This contains all step data from the wizard
}) => {
    // Remove local submission state since this is now handled by wizard
    // const [isSubmitting, setIsSubmitting] = useState(false);

    // No need to call onStepDataChange in preview step - we're just displaying data

    // Extract data from different steps
    const basicInfo = allStepData.basicInfo || stepData.basicInfo || {};
    const assetSelection = allStepData.assetSelection || stepData.assetSelection || {};
    const physicalConnection = allStepData.physicalConnection || stepData.physicalConnection || {};

    // Transform wizard data to API payload format
    // Using primitive dependencies instead of complex objects to prevent infinite renders
    const transformToApiPayload = useCallback(() => {
        const selectedAssets = assetSelection.selectedAssets || assetSelection.addedAssets || [];
        const physicalConnectionAssets = physicalConnection.physicalConnection || physicalConnection.connections || [];
        const outputRatios = physicalConnection.outputRatios || {};

        // Helper function to find driving asset ID (asset immediately above in physical connection)
        const getDrivingAssetId = (currentAssetId, physicalConnectionArray) => {
            const currentIndex = physicalConnectionArray.findIndex(asset =>
                (asset.assetId || asset.id || asset.assetID)?.toString() === currentAssetId?.toString()
            );

            // If asset is first in chain or not found, return null
            if (currentIndex <= 0) {
                return null;
            }

            // Return the assetId of the asset immediately above (previous in array)
            const previousAsset = physicalConnectionArray[currentIndex - 1];
            const assetId = Number(previousAsset.assetId || previousAsset.id || previousAsset.assetID);
            return assetId || null;
        };

        // Helper function to find the nearest Motor asset above in the physical connection chain
        const getDrivingMotorId = (currentAssetId, physicalConnectionArray) => {
            const currentIndex = physicalConnectionArray.findIndex(asset =>
                (asset.assetId || asset.id || asset.assetID)?.toString() === currentAssetId?.toString()
            );

            // If asset is first in chain or not found, return null
            if (currentIndex <= 0) {
                return null;
            }

            // Search backwards in the array to find the first Motor asset
            for (let i = currentIndex - 1; i >= 0; i--) {
                const asset = physicalConnectionArray[i];
                const assetType = (asset.type || asset.assetType || asset.originalAsset?.assetType || '').toLowerCase();

                if (assetType === 'motor') {
                    const motorId = Number(asset.assetId || asset.id || asset.assetID);
                    return motorId || null;
                }
            }

            // No motor found above this asset
            return null;
        };

        // Separate connected and not connected assets based on physical connection
        const connectedAssetIds = physicalConnectionAssets.map(asset =>
            (asset.assetId || asset.id || asset.assetID)?.toString()
        ).filter(Boolean);

        // Regular connected assets (from Step 2 selections that are in physical connection)
        const regularConnectedAssets = selectedAssets
            .filter(asset => {
                const assetId = (asset.assetId || asset.id || asset.assetID)?.toString();
                return assetId && connectedAssetIds.includes(assetId);
            })
            .map(asset => {
                const assetId = asset.assetId || asset.id || asset.assetID;
                const assetIdStr = assetId?.toString();

                return {
                    assetId: Number(assetId),
                    assetType: asset.assetType || asset.type || asset.assetTypeName,
                    assetName: asset.assetName || asset.name || asset.assetDisplayName,
                    typeConfigId: asset.typeConfigId || '',
                    speedRatio: outputRatios[assetId] ? Number(outputRatios[assetId]) : null,
                    drivingAssetId: getDrivingAssetId(assetIdStr, physicalConnectionAssets),
                    drivingMotorId: getDrivingMotorId(assetIdStr, physicalConnectionAssets),
                    subType: asset.selectedSubType || asset.subType || asset.subTypeName || ''
                };
            });

        // Unmonitored gearboxes added via the Add Gearbox dialog.
        // They are NOT in selectedAssets (Step 2), they live only in physicalConnection.
        // Send assetId: -1 so the backend knows this is an unmonitored gearbox.
        const unmonitoredGearboxAssets = physicalConnectionAssets
            .filter(asset => asset.isUnmonitoredGearbox)
            .map(asset => {
                const assetIdStr = (asset.assetId || asset.id)?.toString();
                return {
                    assetId: -1, // -1 indicates unmonitored gearbox to the backend
                    assetName: asset.name || asset.assetName,
                    assetType: asset.originalAsset?.assetType || 'GenericMachine',
                    typeConfigId: UNMONITORED_GEARBOX_TYPE_CONFIG_ID,
                    speedRatio: outputRatios[asset.assetId] ? Number(outputRatios[asset.assetId]) : null,
                    drivingAssetId: getDrivingAssetId(assetIdStr, physicalConnectionAssets),
                    drivingMotorId: getDrivingMotorId(assetIdStr, physicalConnectionAssets),
                    subType: asset.selectedSubType || 'Gearbox',
                    isUnmonitoredGearbox: true,
                    // Metadata required for creating the asset on the backend
                    ...asset.gearboxMetadata?.organization && {
                        organizationId: asset.gearboxMetadata.organization?.organizationID || asset.gearboxMetadata.organization?.id
                    },
                    ...asset.gearboxMetadata?.site && {
                        siteId: asset.gearboxMetadata.site?.siteId || asset.gearboxMetadata.site?.id
                    },
                    ...asset.gearboxMetadata?.assetAccessGroup && {
                        assetAccessGroup: asset.gearboxMetadata.assetAccessGroup
                    }
                };
            });

        const connectedAssets = [...regularConnectedAssets, ...unmonitoredGearboxAssets];

        const notConnectedAssets = selectedAssets
            .filter(asset => {
                const assetId = (asset.assetId || asset.id || asset.assetID)?.toString();
                return !assetId || !connectedAssetIds.includes(assetId);
            })
            .map(asset => ({
                assetId: Number(asset.assetId || asset.id || asset.assetID),
                assetType: asset.assetType || asset.type || asset.assetTypeName,
                assetName: asset.assetName || asset.name || asset.assetDisplayName,
                typeConfigId: asset.typeConfigId || '',
                drivingAssetId: null,
                drivingMotorId: null,
                subType: asset.selectedSubType || asset.subType || asset.subTypeName || ''
            }));

        // If step was skipped, treat all assets as not connected
        if (physicalConnection.isSkipped) {
            const skippedPayload = {
                powertrainId: mode === 'edit' ? basicInfo.powertrainId || basicInfo.id || 0 : 0,
                powertrainName: basicInfo.powertrainName || basicInfo.name || '',
                siteId: basicInfo.selectedSite?.id || basicInfo.selectedSite?.siteId || basicInfo.siteId || 0,
                locationId: basicInfo.selectedLocation?.id || basicInfo.selectedLocation?.locationId || basicInfo.locationId || 0,
                notConnectedAssets: selectedAssets.map(asset => ({
                    assetId: Number(asset.assetId || asset.id || asset.assetID),
                    assetType: asset.assetType || asset.type || asset.assetTypeName,
                    assetName: asset.assetName || asset.name || asset.assetDisplayName,
                    typeConfigId: asset.typeConfigId || '',
                    drivingAssetId: null,
                    drivingMotorId: null,
                    subType: asset.selectedSubType || asset.subType || asset.subTypeName || ''
                })),
                connectedAssets: [],
                ...mode === 'edit' && { rowVersion: basicInfo.rowVersion || '' }
            };

            return skippedPayload;
        }

        const finalPayload = {
            powertrainId: mode === 'edit' ? basicInfo.powertrainId || basicInfo.id || 0 : 0,
            powertrainName: basicInfo.powertrainName || basicInfo.name || '',
            siteId: basicInfo.selectedSite?.id || basicInfo.selectedSite?.siteId || basicInfo.siteId || 0,
            locationId: basicInfo.selectedLocation?.id || basicInfo.selectedLocation?.locationId || basicInfo.locationId || 0,
            notConnectedAssets,
            connectedAssets,
            ...mode === 'edit' && { rowVersion: basicInfo.rowVersion || '' }
        };

        return finalPayload;
    }, [
        // Use primitive values and lengths instead of complex objects
        basicInfo.powertrainName,
        basicInfo.name,
        basicInfo.powertrainId,
        basicInfo.id,
        basicInfo.selectedSite?.id,
        basicInfo.selectedSite?.siteId,
        basicInfo.siteId,
        basicInfo.selectedLocation?.id,
        basicInfo.selectedLocation?.locationId,
        basicInfo.locationId,
        basicInfo.rowVersion,
        mode,
        physicalConnection.isSkipped,
        assetSelection.selectedAssets?.length || 0,
        assetSelection.addedAssets?.length || 0,
        physicalConnection.physicalConnection?.length || 0,
        physicalConnection.connections?.length || 0,
        // Track unmonitored gearboxes separately so payload updates when they are added/removed
        physicalConnection.physicalConnection?.filter(a => a.isUnmonitoredGearbox)?.length || 0,
        physicalConnection.outputRatios
    ]);

    // Handle step completion - this is called when wizard Next button is clicked
    const handleStepComplete = useCallback(() => {
        const payload = transformToApiPayload();
        onStepComplete(payload);
    }, [transformToApiPayload, onStepComplete]);

    // Prepare data for display components
    // Use stable dependencies to prevent infinite renders
    const displayData = useMemo(() => {
        const selectedAssets = assetSelection.selectedAssets || assetSelection.addedAssets || [];
        // Ensure physicalConnectionAssets is always an array
        const physicalConnectionData = physicalConnection.physicalConnection || physicalConnection.connections || [];
        const physicalConnectionAssets = Array.isArray(physicalConnectionData) ? physicalConnectionData : [];

        // Separate connected and not connected assets
        const connectedAssetIds = physicalConnectionAssets.map(asset =>
            (asset.assetId || asset.id || asset.assetID)?.toString()
        ).filter(Boolean);

        const connectedAssets = physicalConnectionAssets
            .filter(asset => {
                const assetId = (asset.assetId || asset.id || asset.assetID)?.toString();
                return assetId && connectedAssetIds.includes(assetId);
            })
            .map(asset => ({
                id: (asset.assetId || asset.id || asset.assetID)?.toString() || '',
                assetId: (asset.assetId || asset.id || asset.assetID)?.toString() || '',
                assetName: asset.assetName || asset.name || asset.assetDisplayName || 'Unknown Asset',
                type: (asset.assetType || asset.type)?.toLowerCase() === ALLOWED_ASSET_TYPES[2] ? asset.selectedSubType || asset.subType || asset.type || asset.assetType : asset.type || asset.subType || asset.assetType || asset.assetTypeName || 'Unknown Type',
                subType: (asset.assetType || asset.type)?.toLowerCase() === ALLOWED_ASSET_TYPES[2] ? asset.subType || asset.assetSubType || asset.assetType || asset.selectedSubType || asset.type || 'Unknown Type' : '',
                inputSpeedRatio: 1, // Default value
                outputSpeedRatio: (asset.subType || asset.selectedSubType || asset.assetSubType)?.toLowerCase() === GENERIC_MACHINE_SUBTYPES[0] ? asset.outputSpeedRatio || asset.speedRatio : '',
                isUnmonitoredGearbox: asset.isUnmonitoredGearbox || false
            }));

        const notConnectedAssets = selectedAssets
            .filter(asset => {
                const assetId = (asset.assetId || asset.id || asset.assetID)?.toString();
                return !assetId || !connectedAssetIds.includes(assetId);
            })
            .map(asset => ({
                id: (asset.assetId || asset.id || asset.assetID)?.toString() || '',
                assetId: (asset.assetId || asset.id || asset.assetID)?.toString() || '',
                assetName: asset.assetName || asset.name || asset.assetDisplayName || 'Unknown Asset',
                type: (asset.assetType || asset.type)?.toLowerCase() === ALLOWED_ASSET_TYPES[2] ? asset.selectedSubType || asset.subType || asset.type || asset.assetType : asset.type || asset.subType || asset.assetType || asset.assetTypeName || 'Unknown Type',
                inputSpeedRatio: 1
            }));

        // If step was skipped, all assets are not connected
        if (physicalConnection.isSkipped) {
            const allAssetsAsNotConnected = selectedAssets.map(asset => ({
                id: (asset.assetId || asset.id || asset.assetID)?.toString() || '',
                assetId: (asset.assetId || asset.id || asset.assetID)?.toString() || '',
                assetName: asset.assetName || asset.name || asset.assetDisplayName || 'Unknown Asset',
                type: (asset.assetType || asset.type)?.toLowerCase() === ALLOWED_ASSET_TYPES[2] ? asset.selectedSubType || asset.subType || asset.type || asset.assetType : asset.type || asset.subType || asset.assetType || asset.assetTypeName || 'Unknown Type',
                inputSpeedRatio: 1
            }));

            return {
                connectedAssets: [],
                notConnectedAssets: allAssetsAsNotConnected
            };
        }

        return { connectedAssets, notConnectedAssets };
    }, [
        // Use primitive values instead of complex objects to prevent infinite renders
        assetSelection.selectedAssets?.length || 0,
        assetSelection.addedAssets?.length || 0,
        physicalConnection.physicalConnection?.length || 0,
        physicalConnection.connections?.length || 0,
        physicalConnection.isSkipped,
        // Include a hash or JSON string of outputRatios to detect changes
        JSON.stringify(physicalConnection.outputRatios || {})
    ]);

    return (
        <div className={`preview-step ${isActive ? 'active' : ''}`}>
            <form onSubmit={(e) => {
                e.preventDefault();
                handleStepComplete();
            }}>
                <div className='preview-content'>
                    <div className='summary-sections'>
                        {/* Basic Info Section */}
                        <div className='summary-section'>
                            <PowertrainBasicInfo
                                powertrainName={
                                    basicInfo.powertrainName ||
                                    basicInfo.name ||
                                    allStepData.basicInfo?.powertrainName ||
                                    allStepData.basicInfo?.name ||
                                    'Untitled Powertrain'
                                }
                                siteName={
                                    basicInfo.selectedSite?.name ||
                                    basicInfo.selectedSite?.siteName ||
                                    basicInfo.siteName ||
                                    basicInfo.site?.name ||
                                    allStepData.basicInfo?.selectedSite?.name ||
                                    allStepData.basicInfo?.selectedSite?.siteName ||
                                    allStepData.basicInfo?.siteName ||
                                    'No site selected'
                                }
                                locationName={
                                    basicInfo.selectedLocation?.locationName ||
                                    basicInfo.selectedLocation?.name ||
                                    basicInfo.locationName ||
                                    basicInfo.location?.name ||
                                    allStepData.basicInfo?.selectedLocation?.locationName ||
                                    allStepData.basicInfo?.selectedLocation?.name ||
                                    allStepData.basicInfo?.locationName ||
                                    'No location selected'
                                }
                                showPlaceholders={true}
                            />
                        </div>

                        {/* Assets & Powertrain Configuration Section */}
                        <div className='summary-section assets-configuration-section'>
                            <PowertrainAssetsConfiguration
                                connectedAssets={displayData.connectedAssets}
                                notConnectedAssets={displayData.notConnectedAssets}
                                showEmptyState={true}
                                maxHorizontalAssets={6}
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

PreviewStep.propTypes = {
    stepData: PropTypes.object,
    onStepComplete: PropTypes.func,
    mode: PropTypes.oneOf(['create', 'edit']),
    isActive: PropTypes.bool,
    allStepData: PropTypes.object
};

export default PreviewStep;
